import React, { useState } from 'react';

const BAL_GRADE = [
  { id: 'N',  label: 'Normal',   color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  { id: 'I',  label: 'Impaired', color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
  { id: 'U',  label: 'Unable',   color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  { id: 'NT', label: 'NT',       color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' },
];

const STATIC_BALANCE = [
  { id: 'sit_eo',      label: 'Sitting',          sub: 'Eyes Open' },
  { id: 'sit_ec',      label: 'Sitting',          sub: 'Eyes Closed' },
  { id: 'stand_eo',    label: 'Standing',         sub: 'Eyes Open' },
  { id: 'stand_ec',    label: 'Standing',         sub: 'Eyes Closed' },
  { id: 'tandem_eo',   label: 'Tandem Standing',  sub: 'Eyes Open' },
  { id: 'tandem_ec',   label: 'Tandem Standing',  sub: 'Eyes Closed' },
  { id: 'one_leg_rt',  label: 'Single Leg Stand', sub: 'Right' },
  { id: 'one_leg_lt',  label: 'Single Leg Stand', sub: 'Left' },
];

const DYNAMIC_BALANCE = [
  { id: 'reaching',     label: 'Reaching Activities', findings: ['Normal','Reduced reach','Falls risk','With compensation','Unable'] },
  { id: 'perturbation', label: 'Perturbation',         findings: ['Normal recovery','Delayed righting','No righting response','Falls','Unable'] },
  { id: 'stepping',     label: 'Stepping Reactions',   findings: ['Normal','Delayed','Absent','Asymmetric'] },
  { id: 'weight_shift', label: 'Weight Shifting',      findings: ['Normal','Reduced to R','Reduced to L','Reduced all dir.','Unable'] },
];

const BalanceAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const get = (key) => value[key] || null;
  const set = (key, val) => onChange?.({ ...value, [key]: value[key] === val ? null : val });
  const toggle = (key, f) => {
    const curr = value[key] || [];
    onChange?.({ ...value, [key]: curr.includes(f) ? curr.filter(x => x !== f) : [...curr, f] });
  };

  const staticAbnormal = STATIC_BALANCE.filter(t => { const g = get(`static_${t.id}`); return g && g !== 'N' && g !== 'NT'; }).length;
  const dynamicAbnormal = DYNAMIC_BALANCE.filter(t => { const v = value[`dyn_${t.id}`] || []; return v.length > 0 && !v.every(f => f === 'Normal'); }).length;
  const bergScore = value.berg_score || '';
  const totalAbnormal = staticAbnormal + dynamicAbnormal;

  const GradeBtn = ({ id, gid }) => {
    const active = get(id) === gid;
    const g = BAL_GRADE.find(x => x.id === gid);
    return (
      <button type="button" onClick={() => set(id, gid)} style={{
        fontSize: '10px', fontWeight: 700, padding: '3px 9px', borderRadius: '6px', cursor: 'pointer',
        transition: 'all 0.15s',
        border: `1px solid ${active ? g.border : '#e2e8f0'}`,
        background: active ? g.bg : 'transparent',
        color: active ? g.color : '#94a3b8'
      }}>{active ? '✓ ' : ''}{g.label}</button>
    );
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px', borderRadius: '14px',
          border: '1px solid #e2e8f0', backgroundColor: 'transparent', color: '#065f46',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', transition: 'all 0.2s'
        }}>
          + Balance Assessment
          {totalAbnormal > 0 && <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#dc2626', color: '#fff', fontWeight: 800 }}>{totalAbnormal} impaired</span>}
          {bergScore && <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#065f46', color: '#fff', fontWeight: 800 }}>Berg: {bergScore}/56</span>}
        </button>
      ) : (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: '800', fontSize: '1rem', color: '#065f46' }}>⚖️ Balance Assessment</span>
            <button type="button" onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }} onMouseEnter={e => e.target.style.color = '#ef4444'} onMouseLeave={e => e.target.style.color = '#cbd5e1'}>×</button>
          </div>

          {/* Summary + Berg Score */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'stretch', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, padding: '12px 16px', borderRadius: '12px', background: totalAbnormal > 0 ? '#fef2f2' : '#ecfdf5', border: `1px solid ${totalAbnormal > 0 ? '#fca5a5' : '#6ee7b7'}` }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: totalAbnormal > 0 ? '#dc2626' : '#059669' }}>
                {totalAbnormal === 0 ? 'No balance deficits documented yet' : `${staticAbnormal} static · ${dynamicAbnormal} dynamic tests impaired`}
              </div>
              <div style={{ display: 'flex', gap: '5px', marginTop: '6px', flexWrap: 'wrap' }}>
                {BAL_GRADE.map(g => <span key={g.id} style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '20px', background: g.bg, color: g.color, border: `1px solid ${g.border}`, fontWeight: 700 }}>{g.label}</span>)}
              </div>
            </div>
            <div style={{ padding: '12px 16px', borderRadius: '12px', background: '#fff', border: '1px solid #e2e8f0', minWidth: '140px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151' }}>Berg Balance Scale</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="number" min="0" max="56" placeholder="0–56"
                  value={bergScore}
                  onChange={e => onChange?.({ ...value, berg_score: e.target.value })}
                  style={{ width: '60px', fontSize: '14px', fontWeight: 800, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#374151', outline: 'none' }}
                />
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>/ 56</span>
              </div>
              {bergScore && <div style={{ fontSize: '10px', color: parseInt(bergScore) >= 41 ? '#059669' : parseInt(bergScore) >= 21 ? '#d97706' : '#dc2626', fontWeight: 700 }}>
                {parseInt(bergScore) >= 41 ? 'Low fall risk' : parseInt(bergScore) >= 21 ? 'Medium fall risk' : 'High fall risk'}
              </div>}
            </div>
          </div>

          {/* STATIC */}
          <div style={{ marginBottom: '12px', overflowX: 'auto', borderRadius: '12px' }}>
          <div style={{ border: '0.5px solid #bbf7d0', minWidth: '360px' }}>
            <div style={{ background: '#dcfce7', padding: '7px 14px', borderBottom: '0.5px solid #bbf7d0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 200px', alignItems: 'center' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Static Balance</div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#166534' }}>Condition</div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#166534', textAlign: 'center' }}>Grade</div>
              </div>
            </div>
            {STATIC_BALANCE.map((t, idx) => {
              const g = get(`static_${t.id}`);
              const gr = BAL_GRADE.find(x => x.id === g);
              return (
                <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 200px', alignItems: 'center', padding: '9px 14px', borderBottom: idx < STATIC_BALANCE.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: (g && g !== 'N' && g !== 'NT') ? '#fff8f8' : '#fff' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151' }}>{t.label}</div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{t.sub}</div>
                    {gr && <div style={{ fontSize: '10px', color: gr.color, fontWeight: 700 }}>{gr.label}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {BAL_GRADE.map(g2 => <GradeBtn key={g2.id} id={`static_${t.id}`} gid={g2.id} />)}
                  </div>
                </div>
              );
            })}
          </div>

          </div>

          {/* DYNAMIC */}
          <div style={{ border: '0.5px solid #fde68a', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ background: '#fefce8', padding: '7px 14px', borderBottom: '0.5px solid #fde68a' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#713f12', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dynamic Balance</div>
            </div>
            {DYNAMIC_BALANCE.map((t, idx) => {
              const selected = value[`dyn_${t.id}`] || [];
              const isAbnormal = selected.length > 0 && !selected.every(f => f === 'Normal');
              return (
                <div key={t.id} style={{ borderBottom: idx < DYNAMIC_BALANCE.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: isAbnormal ? '#fff8f0' : '#fff' }}>
                  <div style={{ padding: '7px 14px 4px', fontSize: '11px', fontWeight: 700, color: '#374151' }}>
                    {t.label}
                    {isAbnormal && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#d97706', fontWeight: 700 }}>⚠ Abnormal</span>}
                  </div>
                  <div style={{ padding: '4px 14px 9px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {t.findings.map(f => {
                      const active = selected.includes(f);
                      const isNormal = f === 'Normal';
                      return (
                        <button key={f} type="button" onClick={() => toggle(`dyn_${t.id}`, f)} style={{
                          fontSize: '11px', fontWeight: 600, padding: '4px 11px', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.15s',
                          border: `1px solid ${active ? (isNormal ? '#6ee7b7' : '#fca5a5') : '#e2e8f0'}`,
                          background: active ? (isNormal ? '#ecfdf5' : '#fef2f2') : 'transparent',
                          color: active ? (isNormal ? '#059669' : '#dc2626') : '#64748b'
                        }}>{active ? '✓ ' : ''}{f}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {Object.keys(value).length > 0 && <button type="button" onClick={() => onChange({})} style={{ marginTop: '10px', fontSize: '12px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>Clear all balance data</button>}
        </div>
      )}
    </div>
  );
};

export default BalanceAssessment;
